import dayjs from 'dayjs';

/**
 * Suma N días calendario a una fecha Dayjs.
 * @param {import('dayjs').Dayjs} fecha
 * @param {number} dias
 * @returns {import('dayjs').Dayjs}
 */
export function sumarDiasCorridos(fecha, dias) {
    return fecha.add(dias, 'day');
}

/**
 * Suma N días hábiles (lunes–viernes, excluye feriados nacionales) a una fecha Dayjs.
 * @param {import('dayjs').Dayjs} fecha
 * @param {number} dias
 * @param {Set<string>} feriados  Set de fechas ISO "YYYY-MM-DD" a excluir
 * @returns {import('dayjs').Dayjs}
 */
export function sumarDiasHabiles(fecha, dias, feriados = new Set()) {
    let resultado = fecha;
    let restantes = dias;
    while (restantes > 0) {
        resultado = resultado.add(1, 'day');
        const diaSemana = resultado.day(); // 0=dom, 6=sab
        const esFeriado = feriados.has(resultado.format('YYYY-MM-DD'));
        if (diaSemana !== 0 && diaSemana !== 6 && !esFeriado) {
            restantes--;
        }
    }
    return resultado;
}

/**
 * Si la fecha cae en fin de semana o feriado, avanza al siguiente día hábil.
 * @param {import('dayjs').Dayjs} fecha
 * @param {Set<string>} feriados
 * @returns {import('dayjs').Dayjs}
 */
export function ajustarADiaHabil(fecha, feriados = new Set()) {
    let resultado = fecha;
    while (
        resultado.day() === 0 ||
        resultado.day() === 6 ||
        feriados.has(resultado.format('YYYY-MM-DD'))
    ) {
        resultado = resultado.add(1, 'day');
    }
    return resultado;
}

/**
 * Calcula la fecha límite de visita del preventor según las reglas SRT.
 *
 * Reglas:
 *   AT Grave  (idTipoSiniestro=1, patologias≠"muerte" y no vacía) → +10 días corridos, ajustado a día hábil
 *   AT Mortal (idTipoSiniestro=1, patologias="muerte")             → +2 o +4 días hábiles según distancia
 *   EP        (idTipoSiniestro=3)                                  → +10 o +50 días corridos, ajustado a día hábil
 *
 * @param {string|null} fechaBase   fecha de alta en ROAM del siniestro, ISO string o null
 * @param {number|null} idTipoSiniestro
 * @param {string|null} patologiasTrazadoras
 * @param {boolean|null} distanciaMayor100km  requerido para AT Mortal y EP
 * @param {Set<string>} feriados  fechas ISO "YYYY-MM-DD" a excluir
 * @returns {import('dayjs').Dayjs|null}
 */
export function calcularFechaLimite(
    fechaBase,
    idTipoSiniestro,
    patologiasTrazadoras,
    distanciaMayor100km,
    feriados = new Set()
) {
    if (!fechaBase) return null;

    const base = dayjs(fechaBase.includes('T') ? fechaBase.split('T')[0] : fechaBase);
    if (!base.isValid()) return null;

    const patNorm = patologiasTrazadoras?.trim().toLowerCase() ?? '';
    const esMortal = patNorm === 'muerte';
    const esGrave = idTipoSiniestro === 1 && !esMortal && patNorm !== '';

    if (esGrave) {
        return ajustarADiaHabil(sumarDiasCorridos(base, 10), feriados);
    }

    if (esMortal) {
        if (distanciaMayor100km === null || distanciaMayor100km === undefined) return null;
        return distanciaMayor100km
            ? sumarDiasHabiles(base, 4, feriados)
            : sumarDiasHabiles(base, 2, feriados);
    }

    if (idTipoSiniestro === 3) {
        if (distanciaMayor100km === null || distanciaMayor100km === undefined) return null;
        return ajustarADiaHabil(
            distanciaMayor100km ? sumarDiasCorridos(base, 50) : sumarDiasCorridos(base, 10),
            feriados
        );
    }

    return null;
}

/**
 * Calcula la fecha normativa SRT (plazo legal) según las reglas de la resolución.
 *
 * Reglas (distintas a calcularFechaLimite que calcula el plazo del Preventor):
 *   AT Grave  (idTipoSiniestro=1, patologias≠"muerte" y no vacía) → +20 días corridos
 *   AT Mortal (idTipoSiniestro=1, patologias="muerte") ≤100km     → +3 días hábiles
 *   AT Mortal (idTipoSiniestro=1, patologias="muerte") >100km     → +7 días hábiles
 *   EP y otros                                                     → null (sin plazo normativo)
 *
 * @param {string|null} fechaDenuncia   ISO string o null
 * @param {number|null} idTipoSiniestro
 * @param {string|null} patologiasTrazadoras
 * @param {boolean|null} distanciaMayor100km  requerido para AT Mortal
 * @param {Set<string>} feriados  fechas ISO "YYYY-MM-DD" a excluir (para AT Mortal)
 * @returns {import('dayjs').Dayjs|null}
 */
export function calcularFechaVencimientoSRT(
    fechaDenuncia,
    idTipoSiniestro,
    patologiasTrazadoras,
    distanciaMayor100km,
    feriados = new Set()
) {
    if (!fechaDenuncia) return null;

    const base = dayjs(fechaDenuncia.includes('T') ? fechaDenuncia.split('T')[0] : fechaDenuncia);
    if (!base.isValid()) return null;

    const patNorm = patologiasTrazadoras?.trim().toLowerCase() ?? '';
    const esMortal = patNorm === 'muerte';
    const esGrave = idTipoSiniestro === 1 && !esMortal && patNorm !== '';

    if (esGrave) {
        return sumarDiasCorridos(base, 20);
    }

    if (esMortal) {
        if (distanciaMayor100km === null || distanciaMayor100km === undefined) return null;
        return distanciaMayor100km
            ? sumarDiasHabiles(base, 7, feriados)
            : sumarDiasHabiles(base, 3, feriados);
    }

    return null;
}
